import React, {FC, useCallback, useRef, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import {Icon, KlarnaBadge} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';

import {STEPS} from '../../constants/checkout';
import {PAYMENTS} from '../../constants/paymentStep';
import {useGetErrorAndSendAnalytics} from '../../hooks/primer/useGetErrorAndSendAnalytics/useGetErrorAndSendAnalytics';
import {usePrimerKlarnaNativeButton} from '../../hooks/primer/usePrimerKlarnaNativeButton';
import {getCurrentStep} from '../../store/selectors/currentStep/currentStep';
import {getPaymentMethodSaveLoading} from '../../store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';
import {getIsSelectedByKey} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {PlaceOrderForKlarnaConnected} from '../placeOrderForKlarna/placeOrderForKlarna';

import {primerKlarnaOptions} from './constants';

import s from './paymentKlarnaPrimer.scss';

export type KlarnaTitlePropsType = {
    isLoading: boolean;
    name?: Maybe<string>;
};

export type PropsType = {
    isSelected: boolean;
    isLoading: boolean;
};

/**
 * Component for display title and badge
 * @param {KlarnaTitlePropsType} props - current props
 * @return {JSX.Element} component
 */
export const KlarnaTitle: FC<KlarnaTitlePropsType> = ({isLoading, name}) => {
    if (isLoading) return <Loader />;

    return (
        <div className={s.title}>
            <Icon height={28} icon={KlarnaBadge} width={67} />
            {name && <span className={s.name}>{name}</span>}
        </div>
    );
};

/**
 * Component for payment by klarna
 * @param {PropsType} props - current props
 * @return {React.Element} component
 */
const PaymentKlarnaPrimer: FC<PropsType> = ({isLoading, isSelected}) => {
    const onSubmitRef = useRef<(() => void) | null>(null);
    const [isAvailable, setIsAvailable] = useState(false);

    const {isValidating} = usePrimerKlarnaNativeButton({
        onPaymentMethodCategoriesChange: useCallback((categories, manager) => {
            if (categories.length > 0) {
                manager.renderCategory({
                    containerId: primerKlarnaOptions.id,
                    // required by Primer SDK types, but we don't need it
                    onHeightChange: () => {},
                    paymentMethodCategoryId: categories[0].id,
                });
                setIsAvailable(true);
                onSubmitRef.current = () => {
                    manager.start({
                        paymentMethodCategoryId: categories[0].id,
                    });
                };
            }
        }, []),
    });

    const {error} = useGetErrorAndSendAnalytics(PAYMENTS.METHOD_PRIMER_KLARNA);

    const sendAnalytics = () => {
        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: 'CTA - Klarna Primer',
                method: 'Payment',
            },
            true,
        );
    };

    return (
        <PaymentWrapperConnected
            className={classNames({
                [s.hide]: !isAvailable,
            })}
            code={PAYMENTS.METHOD_PRIMER_KLARNA}
            isSelected={isSelected}
            title={<KlarnaTitle isLoading={isLoading} />}
        >
            {!error && <div className={s.btnWrapper} id={primerKlarnaOptions.id} />}
            {!error && isAvailable && (
                <PlaceOrderForKlarnaConnected
                    onSubmit={() => {
                        onSubmitRef.current?.();
                        sendAnalytics();
                    }}
                    isLoading={isLoading || isValidating}
                />
            )}
            {error && <p className={s.errorMessage}>{error}</p>}
        </PaymentWrapperConnected>
    );
};

/**
 * Renders klarna primer method only in payment step
 */
export const PaymentKlarnaPrimerContainer: FC<
    PropsType & {
        currentStep: STEPS;
    }
> = ({currentStep, ...props}) => {
    if (currentStep !== STEPS.PAYMENT_STEP) return null;

    return <PaymentKlarnaPrimer {...props} />;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    currentStep: getCurrentStep(store),
    isLoading: getPaymentMethodSaveLoading(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_PRIMER_KLARNA),
});

export const PaymentKlarnaPrimerConnected = connect(mapStateToProps)(PaymentKlarnaPrimerContainer);
